import CommentExtension from '@sereneinserenade/tiptap-comment-extension';
import Underline from '@tiptap/extension-underline';
import { Selection } from '@tiptap/pm/state';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import CommentCard from '~/components/common/comments/commentCard';
import CommentTextField from '~/components/common/comments/commentTextField';
import { TextStyleExtended } from '~/components/common/editor/extensions/FontSize';
import CommentsDrawer from '~/components/customer/settings/program/comments/commentsDrawer';
import ControlledBubbleMenu from '~/components/customer/settings/program/comments/controlledMenu/controlledBubbleMenu';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { Comment } from '~/interfaces/Comment';
import { CommentReply } from '~/interfaces/CommentReply';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { TenantService } from '~/services/api/tenant/endpoints';
import { CommentResponse, TabType } from '~/services/api/tenant/types';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

interface PolicyCommentsWrapperProps {
  readonly enableComments: boolean;
  readonly setEnableComments: React.Dispatch<React.SetStateAction<boolean>>;
  readonly policy: {
    id: number;
    content: string;
  };
  readonly children: (data: {
    commentsMode: boolean;
    policyText: string;
  }) => ReactNode;
}

const transformComments = (comments: CommentResponse[]) => {
  return comments.map((comment) => {
    const processedReplies = comment.replies.map((reply) => {
      const processedReply: CommentReply = {
        id: reply.id,
        content: reply.content,
        updatedAt: reply.updated_at,
        isEdited: reply.is_edited,
        authorDetails: reply.author_details,
        taggedUsersDetails: reply.tagged_users_details,
        hasSeenUsers: reply.has_seen_users,
      };
      return processedReply;
    });
    const processedComment: Comment = {
      id: comment.id.toString(),
      content: comment.content,
      highlightedText: comment.highlighted_text,
      isResolved: comment.is_resolved,
      isEdited: comment.is_edited,
      updatedAt: comment.updated_at,
      authorDetails: comment.author_details,
      taggedUsersDetails: comment.tagged_users_details,
      replies: processedReplies,
      hasSeenUsers: comment.has_seen_users,
    };
    return processedComment;
  });
};

export default function PolicyCommentsWrapper({
  enableComments,
  setEnableComments,
  policy,
  children,
}: PolicyCommentsWrapperProps) {
  const slug = useTenantSlug();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const [comments, setComments] = useState<Comment[]>([]);
  const [newPolicyText, setNewPolicyText] = useState(policy.content);
  const [params, setParams] = useState<{
    query?: string;
    mentionsOnly?: string;
  }>({
    query: undefined,
    mentionsOnly: undefined,
  });

  const commentsQuery = TenantService.endpoints.getComments.useQuery({
    slug: slug,
    channelId: selectedChannel ?? 0,
    params: {
      mentions_only: params.mentionsOnly ?? '',
      search: params.query ?? '',
    },
  });

  useEffect(() => {
    if (commentsQuery.data) {
      setComments(transformComments(commentsQuery.data));
    }
  }, [commentsQuery.data]);

  const onChangeFormattedText = (content: string) => {
    setNewPolicyText(content);
  };

  return (
    <>
      {children({
        commentsMode: enableComments,
        policyText: newPolicyText,
      })}
      <CommentsEditor
        policy={policy}
        onChangeText={onChangeFormattedText}
        enableComments={enableComments}
        setCommentState={setEnableComments}
        comments={comments}
        setComments={() => commentsQuery.refetch()}
        searchComments={(query, mentionsOnly) => {
          setParams({ query, mentionsOnly });
        }}
        refreshContent={() =>
          commentsQuery.refetch().then(() => policy.content)
        }
        isLoading={commentsQuery.isFetching}
      />
    </>
  );
}

interface CommentsEditorProps {
  policy: {
    id: number;
    content: string;
  };
  comments: Comment[];
  enableComments: boolean;
  onChangeText: (content: string) => void;
  searchComments: (query: string, mentionsOnly: string) => void;
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  setCommentState: React.Dispatch<React.SetStateAction<boolean>>;
  refreshContent: () => Promise<string>;
  isLoading: boolean;
}

const CommentsEditor = ({
  policy,
  comments,
  enableComments,
  onChangeText,
  setComments,
  setCommentState,
  searchComments,
  refreshContent,
  isLoading,
}: CommentsEditorProps) => {
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const slug = useTenantSlug();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState<Selection | null>(null);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentionsMode, setMentionsMode] = useState('False');
  const commentsSectionRef = useRef<HTMLDivElement | null>(null);

  const UNKNOWN_ERROR_MESSAGE = intl.formatMessage({
    defaultMessage: 'An unknown issue ocurred while processing your request.',
  });

  const updateChannelTabs = useWrappedMutation(
    TenantService.endpoints.updateChannelTabs.useMutation(),
  );
  const addComment = useWrappedMutation(
    TenantService.endpoints.addPolicyComment.useMutation(),
  );
  const removeComment = useWrappedMutation(
    TenantService.endpoints.removePolicyComment.useMutation(),
  );
  const resolveComment = useWrappedMutation(
    TenantService.endpoints.resolvePolicyComment.useMutation(),
  );

  const activeComment = comments.find(
    (comment) => comment.id === activeCommentId,
  );

  const updatePolicy = async (text: string) => {
    await updateChannelTabs.mutateAsync(
      {
        data: {
          tabs: [
            {
              tab_type: TabType.POLICY,
              id: policy.id,
              content: text,
              is_display_on_program_overview_enabled: true,
            },
          ],
        },
        intakeChannelId: selectedChannel ?? 0,
      },
      {
        onError: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'Something went wrong while updating the policy comments.',
            }),
            'error',
          );
        },
      },
    );
  };

  const focusActiveComment = (id: string) => {
    if (!commentsSectionRef) return;

    const commentCardRef =
      commentsSectionRef.current?.querySelector<HTMLDivElement>(
        `div#comment-${id}`,
      );
    if (!commentCardRef) return;

    commentCardRef.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'center',
    });
  };

  const extensions = [
    StarterKit,
    Underline,
    TextStyleExtended,
    CommentExtension.configure({
      HTMLAttributes: {
        class: 'attached-comment',
      },
      onCommentActivated: (commentId) => {
        focusActiveComment(commentId);
        setActiveCommentId(commentId);
      },
    }),
  ];

  const initialEditor = useEditor({
    editable: false,
    extensions: extensions,
    onUpdate: ({ editor }) => {
      onChangeText(editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
      const { view, state } = editor;
      const { from, to } = view.state.selection;
      const retrievedText = state.doc.textBetween(from, to, '');
      setSelectedText(retrievedText);
      setSelectionRange(view.state.selection);
    },
    content: policy.content,
  });

  if (!initialEditor) {
    return <></>;
  }

  const refreshAllContent = async () => {
    const newContent = await refreshContent();
    if (newContent) {
      initialEditor.commands.setContent(newContent);
    }
  };

  const appendCommentToText = async (id: string) => {
    if (selectionRange) {
      initialEditor.commands.setTextSelection({
        from: selectionRange.from,
        to: selectionRange.to,
      });
    }

    initialEditor.commands.setComment(id);
    focusActiveComment(id);
    initialEditor.commands.setTextSelection(0);
    updatePolicy(initialEditor.getHTML());
  };

  const setNewComment = async (content: string, taggedUserIDs: number[]) => {
    if (!initialEditor) {
      return;
    }
    const commentBody = {
      content: content,
      highlighted_text: selectedText,
      tagged_users: taggedUserIDs,
    };
    await addComment.mutateAsync(
      {
        slug: slug,
        channelId: selectedChannel ?? 0,
        payload: commentBody,
      },
      {
        onSuccess: (response) => {
          searchComments(searchQuery, mentionsMode);
          appendCommentToText(response.id.toString());
        },
        onError: (error) => {
          const errorMessage = getErrorMessage(error);
          createToastNotification(
            errorMessage ?? UNKNOWN_ERROR_MESSAGE,
            'error',
          );
        },
      },
    );
  };

  const markCommentResolved = async (resolvedCommentID: string) => {
    if (!initialEditor) return;

    const commentBody = {
      is_resolved: true,
    };
    await resolveComment.mutateAsync(
      {
        slug: slug,
        channelId: selectedChannel ?? 0,
        commentId: resolvedCommentID,
        payload: commentBody,
      },
      {
        onSuccess: () => {
          initialEditor.commands.unsetComment(resolvedCommentID);
          updatePolicy(initialEditor.getHTML());
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Comment updated. ' }),
            'success',
          );
        },
        onError: (error) => {
          const errorMessage = getErrorMessage(error);
          createToastNotification(
            errorMessage ?? UNKNOWN_ERROR_MESSAGE,
            'error',
          );
        },
      },
    );
  };

  const deleteComment = async (deletedCommentID: string) => {
    if (!initialEditor) return;
    await removeComment.mutateAsync(
      {
        slug: slug,
        channelId: selectedChannel ?? 0,
        commentId: deletedCommentID,
      },
      {
        onSuccess: () => {
          initialEditor.commands.unsetComment(deletedCommentID);
          updatePolicy(initialEditor.getHTML());
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Comment deleted. ' }),
            'success',
          );
        },
        onError: (error) => {
          const errorMessage = getErrorMessage(error);
          createToastNotification(
            errorMessage ?? UNKNOWN_ERROR_MESSAGE,
            'error',
          );
        },
      },
    );
  };

  const cancelComment = () => {
    initialEditor?.commands.setTextSelection(0);
  };

  const searchAndFilterComments = async (
    query: string,
    mentionsOnly: string,
  ) => {
    searchComments(query, mentionsOnly);
  };

  const refreshAndFilterComments = async (
    query: string,
    mentionsOnly: string,
  ) => {
    await refreshAllContent();
    await searchAndFilterComments(query, mentionsOnly);
  };

  return (
    <>
      <div
        className={clsx(
          'flex flex-col border rounded-md h-[620px]',
          'focus-within:border-[#181818]',
          {
            hidden: !enableComments,
            'bg-white': initialEditor.isFocused,
          },
          'border-[#DADADA]',
        )}
      >
        <div className='ml-1 overflow-y-auto max-h-full h-full min-h-44'>
          <EditorContent editor={initialEditor} />
        </div>
        <BubbleMenu
          className='commentMenu'
          shouldShow={({ editor }) => editor.isActive('comment')}
          editor={initialEditor}
          tippyOptions={{
            placement: 'bottom',
          }}
        >
          {activeComment && (
            <CommentCard
              isActive
              comment={activeComment}
              setComments={setComments}
              resolveComment={markCommentResolved}
              deleteComment={deleteComment}
            />
          )}
        </BubbleMenu>
        <ControlledBubbleMenu
          placement='bottom'
          editor={initialEditor}
          open={Boolean(selectedText)}
        >
          <div className='commentMenu'>
            <CommentTextField
              handleAddComment={setNewComment}
              handleCancelComment={cancelComment}
            />
          </div>
        </ControlledBubbleMenu>
      </div>
      <CommentsDrawer
        comments={comments}
        setComments={setComments}
        isLoading={isLoading}
        searchQuery={searchQuery}
        ref={commentsSectionRef}
        enabledComments={enableComments}
        activeCommentID={activeCommentId}
        mentionsMode={mentionsMode}
        setMentionsMode={setMentionsMode}
        setSearchQuery={setSearchQuery}
        resolveComment={markCommentResolved}
        deleteComment={deleteComment}
        setCommentState={setCommentState}
        fetchSearchedComments={refreshAndFilterComments}
      />
    </>
  );
};
